<template>
  <div>
    <draggable
      :list="list"
      class="tour-day-item-container d-flex flex-wrap align-items-center"
      draggable=".item-container:not(.not-draggable)"
      item-key="id"
      v-bind="dragOptions"
      @change="onChanged"
    >
      <template #item="{ element }">
        <div
          :class="{
            'not-draggable ghost-item': element.disabled || updatingTour,
            'not-draggable blocked-item': element.isBlocked || isSold,
            'item-selected': element.id === currentTourDayItem?.id,
          }"
          class="item-container position-relative mb-12 me-16"
        >
          <!-- Actions on Top -->
          <div class="tour-day-item-actions d-flex justify-content-center mb-4">
            <v-button
              :disabled="updatingTour || isSold"
              class="p-1 bg-white border rounded-circle shadow-sm me-4"
              size="xs"
              variant="icon"
              @click="hideTravelDoc(element)"
            >
              <v-icon :icon="element.hide_from_travel_doc ? 'eye-blocked' : 'eye'" size="xs" class="text-primary" />
            </v-button>

            <v-button
              :disabled="updatingTour"
              class="p-1 bg-white border rounded-circle shadow-sm me-4"
              size="xs"
              variant="icon"
              @click="setCurrentItem(element)"
            >
              <v-icon icon="pencil" size="xs" class="text-primary" />
            </v-button>

            <v-button
              :disabled="updatingTour || isSold"
              class="p-1 bg-white border rounded-circle shadow-sm"
              size="xs"
              variant="icon"
              @click="deleteItem(element, !!element.supplier ? element.supplier : null)"
            >
              <v-icon icon="trash" size="xs" class="text-danger" />
            </v-button>
          </div>

          <!-- Icon in Middle (Square Shape) -->
          <div class="d-flex justify-content-center">
            <div
              :class="{
                'not-draggable ghost-hotel-item': element.disabled,
                'blocked-item': element.isBlocked || isSold,
                'has-error': element.has_error,
              }"
              class="tour-day-item d-flex align-items-center justify-content-center position-relative tooltip-wrapper bg-white border rounded shadow-sm"
              style="width: 3.5rem; height: 3.5rem;"
            >
              <v-icon
                :icon="searchItem(element?.type)?.icon || 'question'"
                class="text-xxl service-icon"
                :style="{ color: getServiceIconColor(element?.type) }"
              />

              <div class="status-container">
                <span
                  v-if="element.disabled"
                  class="position-absolute top-0 start-0 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                  :style="{ backgroundColor: 'var(--uq-secondary)', width: '16px', height: '16px' }"
                >
                  <v-icon icon="lock" class="text-white" :style="{ fontSize: '9px' }" />
                  <span class="visually-hidden"> {{ $t("tourBuilder.payment") }} {{ element.paymentStatus }} </span>
                </span>

                <template v-else-if="!!element.supplier">
                  <template v-if="element.type !== 'stage_information' && element.type !== 'general_information'">
                    <span
                      v-if="!!element.payment_status"
                      :style="{
                        backgroundColor: `rgba(var(--uq-${findConceptByKey('tour.payment_status_type', element.payment_status).color}-rgb), 0.15)`,
                        width: '16px',
                        height: '16px',
                      }"
                      :title="`${$t('tourBuilder.payment')} ${element.payment_status}`"
                      class="position-absolute top-0 start-0 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                      <v-icon
                        :icon="findConceptByKey('tour.payment_status_type', element.payment_status).icon"
                        :style="{ color: `var(--uq-${findConceptByKey('tour.payment_status_type', element.payment_status).color})`, fontSize: '9px' }"
                      />
                      <span class="visually-hidden">
                        {{ $t("tourBuilder.payment") }} {{ element.payment_status }}
                      </span>
                    </span>

                    <span
                      v-if="!!element.invoice_status"
                      :style="{
                        backgroundColor: `rgba(var(--uq-${findConceptByKey('tour.invoice_status_type', element.invoice_status).color}-rgb), 0.15)`,
                        width: '16px',
                        height: '16px',
                      }"
                      :title="`${$t('tourBuilder.invoice')} ${element.invoice_status}`"
                      class="position-absolute top-0 start-100 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                      <v-icon
                        :icon="findConceptByKey('tour.invoice_status_type', element.invoice_status).icon"
                        :style="{ color: `var(--uq-${findConceptByKey('tour.invoice_status_type', element.invoice_status).color})`, fontSize: '9px' }"
                      />
                      <span class="visually-hidden">
                        {{ $t("tourBuilder.invoice") }} {{ element.invoice_status }}
                      </span>
                    </span>
                    <span
                      v-if="!!element.booking_status && !(element.type === 'transport' && element.booking_status === 'connected')"
                      :style="{
                        backgroundColor: `rgba(var(--uq-${findConceptByKey('tour.reservation_status_type', element.booking_status).color}-rgb), 0.15)`,
                        width: '16px',
                        height: '16px',
                      }"
                      :title="`${$t('tourBuilder.reservation')} ${element.booking_status}`"
                      class="position-absolute top-100 start-100 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                      <v-icon
                        :icon="findConceptByKey('tour.reservation_status_type', element.booking_status).icon"
                        :style="{ color: `var(--uq-${findConceptByKey('tour.reservation_status_type', element.booking_status).color})`, fontSize: '9px' }"
                      />
                      <span class="visually-hidden">
                        {{ $t("tourBuilder.reservation") }} {{ element.booking_status }}
                      </span>
                    </span>
                    <span
                      v-if="element.objectType === 'hotel' && element.supplier === '-'"
                      :title="`${$t('tourBuilder.hotelRoomTypeUnavailable')}`"
                      class="position-absolute top-100 start-0 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                      :style="{ backgroundColor: 'var(--uq-warning)', width: '16px', height: '16px' }"
                    >
                      <v-icon icon="warning-sign" class="text-white" :style="{ fontSize: '9px' }" />
                      <span class="visually-hidden">
                        {{ $t("tourBuilder.reservation") }} {{ element.booking_status }}
                      </span>
                    </span>
                  </template>
                </template>

                <span
                  v-if="!element.disabled && isTransportLuggageConnected(element)"
                  :title="$t('tourBuilder.luggageConnected')"
                  class="position-absolute top-100 start-0 translate-middle rounded-circle d-inline-flex align-items-center justify-content-center"
                  :style="{ backgroundColor: 'var(--uq-success)', width: '16px', height: '16px' }"
                >
                  <v-icon icon="luggage-cart" class="text-white" :style="{ fontSize: '9px' }" />
                  <span class="visually-hidden">{{ $t("tourBuilder.luggageConnected") }}</span>
                </span>
              </div>
              <div class="tooltip-text" v-if="element.has_error">
                {{ element.error_remark }}
              </div>
            </div>
          </div>

          <!-- Name on Bottom -->
          <div
            class="supplier-name text-xs text-center fw-medium text-dark mt-2 mx-auto"
            :title="element.supplier"
            style="width: 4.25rem; word-break: break-word; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;"
          >
            <template v-if="element.type === 'stage_information'">
              {{ element?.item_data?.title }}
            </template>
            <template v-else-if="element.type === 'general_information'">
              {{ element?.item_data?.title }}
            </template>
            <template v-else-if="element.type === 'transfer' && element.supplier === ''">
              {{ $t("tourBuilder.transferNotSelected") }}
            </template>
            <template v-else-if="element.type === 'transport' && element.supplier === ''">
              {{ $t("tourBuilder.transportNotSelected") }}
            </template>
            <template v-else>
              {{ element.supplier === "-" ? "" : element.supplier }}
            </template>
          </div>

        </div>
      </template>

      <template #footer>
        <div class="ms-8 d-flex align-items-center align-self-center" style="height: 3.5rem;">
          <v-button
            :disabled="updatingTour || !canAddItem || isSold"
            class="btn-add-plus rounded-circle shadow-sm d-flex align-items-center justify-content-center border-0 p-0"
            size="xs"
            variant="primary"
            @click="openModal('the-tour-day-item-create-modal')"
          >
            <v-icon icon="plus" class="m-0" style="font-size: 10px; line-height: 1;" />
          </v-button>
        </div>
      </template>
    </draggable>

    <the-tour-day-item-create-modal
      v-if="modalToShow === 'the-tour-day-item-create-modal'"
      version="new"
      :agency="agency"
      :client-tour-id="clientTourId"
      :tour-day-id="tourDayId"
      :tour-id="tourId"
      @closed="closeModal"
      @emit-reload-tour="emitReloadTour"
      @open-details="setCurrentItem"
    />
  </div>
</template>

<script lang="ts" setup name="TheCaseDayItemDraggable">
import { computed, PropType, getCurrentInstance } from "vue";
import Vuedraggable from "vuedraggable";
import { TYPE, useToast } from "vue-toastification";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import useModalUtils from "@/helpers/ModalUtils";
import TheTourDayItemCreateModal from "@/components/tour/TheTourDayItemCreateModal.vue";
import { useUserStore } from "@/stores/user";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import Concept from "@/api/objects/Concept";
import { useI18n } from "vue-i18n";
import { getServiceIconColor } from "@/helpers/ServiceIconColor";

const draggable = Vuedraggable;

const emit = defineEmits([
  "emitReloadTour",
  "openItemDetailsOffCanvas",
  "moveItemToAnotherDay",
  "closeItemDetailsOffCanvas",
]);

const props = defineProps({
  list: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  tourDayId: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  clientTourId: {
    type: Number,
    required: true,
  },
  tourId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  canAddItem: {
    type: Boolean,
    required: false,
    default: true,
  },
  tourMapped: {
    type: Object as PropType<Record<string, unknown>>,
    required: true,
  },
  isSold: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;

const { openModal, closeModal, modalToShow } = useModalUtils();
const { offCanvasToShow, closeOffCanvas, openOffCanvas } = useOffCanvasUtils();
const concepts = useConcepts();
const userStore = useUserStore();

const currentTourDayItem = computed(() => userStore.currentTourDayItem);
const currentTour = computed(() => userStore.currentTour);

const itemTypes = computed(() => {
  if (concepts.concepts.value && concepts.concepts.value["supplier.type"]) {
    return mapConcepts(concepts.concepts.value["supplier.type"]);
  }
  return [];
});

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day-item",
  disabled: false,
  ghostClass: "ghost",
  preventOnFilter: false,
}));

const onChanged = ({ moved, added }: any) => {

  // 🔹 Handle moved
  if (moved) {
    const templateDay = props?.tourMapped?.case_days.find((caseDay: any) => caseDay.id === moved.element.case_day_id);

    if (templateDay) {
      const itemReplaced = templateDay.case_day_items[moved.newIndex];

      moveItemPosition(moved.element, itemReplaced.position);
    }
  }

  // 🔹 Handle added
  if (added) {
    const dayItemsWithNewItem = props?.list?.filter((item: any) => item.disabled || item.id !== added.element.id);

    if (dayItemsWithNewItem) {
      const itemReplaced = dayItemsWithNewItem[added.newIndex];

      if (itemReplaced) {
        emit("moveItemToAnotherDay", {
          element: added.element,
          newPosition: itemReplaced.position,
          oldElement: added.element
        });
      } else {
        const lastElement: any = dayItemsWithNewItem.pop();

        if (lastElement) {
          emit("moveItemToAnotherDay", {
            element: added.element,
            newPosition: lastElement.position + 1,
            oldElement: added.element
          });
        } else {
          emit("moveItemToAnotherDay", {
            element: added.element,
            newPosition: 1,
            oldElement: added.element
          });
        }
      }
    }
  }
};

const moveItemPosition = async (item: Record<string, any>, newPosition: number) => {

  try {
    const data = { position: newPosition };

    await api.case.movePosition(props.agency, props.tourId, props.tourDayId, item.id, data);

    toast.success(t("general.shared.savedChanges"));

    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response.data.message);
    emit("emitReloadTour");
  }
};

const setCurrentItem = (item: Record<string, any>) => {
  const caseDayItem = item?.case_days?.find((caseDay: any) => caseDay.id === props.tourDayId);
  const lastCaseDayItem = caseDayItem?.case_day_items?.length
    ? caseDayItem.case_day_items.reduce((last: { position: number }, current: { position: number }) =>
        current.position > last.position ? current : last
      )
    : null;

  const data = {
    agency: props.agency,
    caseId: props.tourId,
    caseDayId: item.case_day_id ?? props.tourDayId,
    caseDayItemId: item.case_day_id ? item.id : lastCaseDayItem?.id,
  };

  localStorage.setItem("currentTourDayItem", JSON.stringify(data));

  emit("openItemDetailsOffCanvas", data);
  emit("emitReloadTour");
};

const searchItem = (type: string) => {
  return itemTypes.value.find((itemType: Concept) => itemType.key === type);
};

const LUGGAGE_HOTEL_TYPES = ["hotel", "luggage"];

// Mirrors CaseActionController::calculateLuggageConnections: walk the days in
// order and detect live hotel transitions. A connection counts as live only
// when the day's transport resources still point at the current hotel pair —
// stale connections (itinerary changed after connecting) don't qualify.
const luggageConnections = computed(() => {
  const days = [...(props.tourMapped?.case_days ?? [])].sort(
    (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
  );

  const connections: { originId: any; destinationId: any; transportItemId: any }[] = [];
  let prevHotelId: number | string | null = null;

  for (const day of days) {
    const items = day.case_day_items ?? [];
    const hotelItem = items.find((item: any) =>
      LUGGAGE_HOTEL_TYPES.includes(String(item.type).toLowerCase())
    );
    const currHotelId = hotelItem?.model_id ?? null;

    if (currHotelId && prevHotelId && String(currHotelId) !== String(prevHotelId)) {
      const transportItem = items.find(
        (item: any) => String(item.type).toLowerCase() === "transport"
      );
      const resources = transportItem?.case_day_item_resources ?? [];

      const isLiveConnection =
        resources.length > 0 &&
        resources.every(
          (resource: any) =>
            !!resource.is_connected_by_system &&
            String(resource.origin_id) === String(prevHotelId) &&
            String(resource.destination_id) === String(currHotelId)
        );

      if (isLiveConnection) {
        connections.push({
          originId: prevHotelId,
          destinationId: currHotelId,
          transportItemId: transportItem.id,
        });
      }
    }

    if (currHotelId) {
      prevHotelId = currHotelId;
    }
  }

  return connections;
});

const isHotelConnectedToLuggage = (caseDayItem: Record<string, any>): boolean => {
  if (!LUGGAGE_HOTEL_TYPES.includes(String(caseDayItem.type).toLowerCase())) {
    return false;
  }

  // Connect-luggage stores the hotel item's model_id (the supplier id) in
  // origin_id/destination_id of the day's transport resources. A hotel counts
  // as connected only when it is the ORIGIN of a live connection — i.e.
  // luggage departs this hotel to the next one. The last day's hotel is only
  // ever a destination and never triggers a warning.
  const hotelId = caseDayItem.model_id;

  return !!hotelId && luggageConnections.value.some(
    (connection) => String(connection.originId) === String(hotelId)
  );
};

const isTransportLuggageConnected = (caseDayItem: Record<string, any>): boolean => {
  return luggageConnections.value.some(
    (connection) => connection.transportItemId === caseDayItem.id
  );
};

const deleteItem = async (caseDayItem: Record<string, any>, name: null | string = null) => {
  const dayItem = name ? name : findConceptByKey("supplier.type", caseDayItem.type).name;

  await $modal?.delete({
    title: t("tourBuilder.deleteTourDayItemTitle"),
    text: t(
      isHotelConnectedToLuggage(caseDayItem)
        ? "tourBuilder.deleteTourDayItemLuggageText"
        : "tourBuilder.deleteTourDayItemText",
      { dayItem }
    ),
    deleteButtonText: t("general.button.delete"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doDeleteItem(caseDayItem.id),
  });
};

const doDeleteItem = async (caseDayItemId: number) => {
  const loadingToast = $toast?.loading(t("general.button.deleting"), "toast-remove-tour-day-item");

  try {
    await api.case.caseDayItemRemove(props.agency, props.tourId, props.tourDayId, caseDayItemId);

    emit("emitReloadTour");

    if (currentTourDayItem.value?.id && caseDayItemId === currentTourDayItem.value?.id) {
      emit("closeItemDetailsOffCanvas");
    }

    emit("emitReloadTour");

    loadingToast?.update("toast-remove-tour-day-item", {
      content: t("tourBuilder.dayItemDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast?.update("toast-remove-tour-day-item", {
      content: t("tourBuilder.dayItemDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    console.error(e.response?.data);

    toast.error(e.response?.data?.message);
  }
};

const hideTravelDoc = async (item: Record<string, any>) => {
  try {
    await api.case.caseToggleItemVisibility(props.agency, props.tourId, item.case_day_id, item.id);
    toast.success(t("general.shared.savedChanges"));
    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response?.data?.message);
  }
}

const emitReloadTour = () => {
  emit("emitReloadTour");
};
</script>

<style lang="scss" scoped>
.tour-day-item-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-wrap: wrap;
}

.item-container {
  .tour-day-item {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  }

  .tour-day-item-actions {
    visibility: hidden;
  }

  &:hover:not(.ghost-item) .tour-day-item-actions {
    visibility: visible;
  }

  &:hover:not(.ghost-item) .tour-day-item {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18) !important;
    transform: translateY(-1px);
  }
}

.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.item-selected:not(.ghost-item) {
  transform: translateY(-5px);

  .tour-day-item-actions {
    visibility: visible !important;
  }
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

.supplier-name {
  width: 4.25rem;
  overflow: hidden;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: absolute;
}

.list-group-item {
  display: block;
  padding: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  border-radius: 0;
  width: 100%;
}

.badge-status {
  padding: 2px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 14px;
  height: 14px;

  :deep(.v-icon) {
    font-size: 7px !important;
  }
}

.badge-triangle {
  width: 0;
  height: 0;
  border-left: 13px solid transparent;
  border-right: 14px solid transparent;
  border-bottom: 24px solid #eed202;
  background-color: transparent !important;
  display: flex;
  align-items: end;
  justify-content: center;
}
.badge-triangle .triangle-warning {
  position: absolute;
  top: 8px;
  font-size: 0.75em;
}

.bg-beige {
  opacity: 1;
  background-color: var(--uq-beige-light) !important;
}

.bg-purple {
  background-color: rgba(var(--uq-purple-rgb), 0.95) !important;
}

.bg-light-blue {
  background-color: var(--uq-light-blue) !important;
}

.has-error {
  background-color: #ff7b7b !important;
}

.service-icon {
  transition: color 0.2s ease;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  background-color: #bbe5f1;
  color: black;
  text-align: center;
  padding: 6px 12px;
  border-radius: 6px;
  position: absolute;
  z-index: 1000;
  bottom: 115%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;

  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -6px;
  border-width: 6px;
  border-style: solid;
  border-color: #bbe5f1 transparent transparent transparent;
}

.tooltip-wrapper:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style>
